import { useValueState } from '../../../utils/hooks';
import React, { useContext, useState } from 'react';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import {
  AlertType,
  ICustomerNoteMutationResult,
  ICustomerNoteMutationVariables,
} from '../../../graphql/schema/p4types';
import { useDispatch } from 'react-redux';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { serviceActions } from '../../../state/modules/service';
import getClientMutationId from '../../../utils/getClientMutationId';
import { FluentProvider, Textarea, webLightTheme } from '@fluentui/react-components';
import { BasicHeader } from '../../../actions/BasicHeader';
import QueryDropdown from '../QueryDropdown';
import { ModalBottomButtonContainer } from '../../modals/Styles';
import { PrimaryButton, SecondaryButton } from '../Button';
import { IP4Note, MemoContext, useNoteConfig } from './MemoEditor';
import getIn from '../utils/getIn';
import FluentModal from '../../modals/FluentModal';

export const MemoEdit = ({
  item,
  isOpen,
  onClose,
  path,
}: {
  item?: IP4Note;
  isOpen: boolean;
  onClose: () => void;
  path?: string;
}) => {
  const [modalText, setModalText] = useValueState(item?.text || '');
  const connectedForm = useContext(ConnectedFormContext);

  const { resetList, type, noteTypeEntity } = useContext(MemoContext);
  const { mutationNode, mutationKey, refetchNodes, linkKey } = useNoteConfig(type);
  const handleQueryError = useComponentQueryErrorHandling();
  const [saveNote, { loading }] = useMutation<ICustomerNoteMutationResult, ICustomerNoteMutationVariables>(
    mutationNode,
    {
      onError: handleQueryError,
      refetchQueries: refetchNodes,
    }
  );
  const [noteType, setNoteType] = useState(item?.noteType);

  const dispatch = useDispatch();

  const onClickSave = () => {
    //For notes that support note types, we need to initially the note type onto the record here.
    const note = normalizeVariables(item) || {};

    if (!connectedForm.values?.id) {
      dispatch(
        serviceActions.genericAlert(
          'Notes',
          'Cannot add a note to a new record, please save the record before adding notes.',
          AlertType.Warning
        )
      );
      return;
    }

    let id: number;
    let concurrencyVersion: number;
    let uuid: string;

    if (path) {
      id = getIn(connectedForm.values, path + '.id');
      concurrencyVersion = getIn(connectedForm.values, path + 'concurrencyVersion');
      uuid = getIn(connectedForm.values, path + '.uuid');
    } else {
      id = connectedForm?.values?.id;
      concurrencyVersion = connectedForm?.values?.concurrencyVersion;
      uuid = connectedForm?.values?.uuid;
    }

    const variables = {
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          [mutationKey]: {
            ...note,
            text: modalText,
            ...(noteType ? { noteType: normalizeVariables(noteType) } : {}),
            [linkKey]: {
              id: id,
              concurrencyVersion: concurrencyVersion,
              uuid: uuid,
            },
          },
        },
      },
    };

    saveNote(variables).then((result) => {
      if (result.data) {
        onClose();
        resetList?.();
        dispatch(serviceActions.genericAlert('Note Save', 'Note saved successfully', AlertType.Success));
      }
    });
  };

  //Note - The nested 'FluentProvider' is due to some weird compatability between fluent-ui v8 & v9 components.
  //     - (Could migrate to using the v9 'Dialog' to resolve, but there's some styling quirks to figure out there).
  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '800px', paddingBottom: '60px' } }}>
      <FluentProvider theme={webLightTheme}>
        <BasicHeader>Edit Note</BasicHeader>
        <div style={{ padding: '1em' }}>
          {noteTypeEntity ? (
            <QueryDropdown
              label={'Note Type'}
              {...noteTypeEntity.dropdown}
              onChange={setNoteType}
              value={noteType}
              data-testid={'memo-edit-note-type'}
              mandatory={true}
            />
          ) : null}
          <Textarea
            style={{ width: '100%', height: '200px' }}
            value={modalText}
            onChange={(ev, data) => setModalText(data.value)}
            data-testid={'memo-note-text-area'}
          />
        </div>
        <ModalBottomButtonContainer>
          <SecondaryButton data-testid={'memo-cancel-note'} onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton data-testid={'memo-save-note'} onClick={onClickSave} disabled={loading}>
            Save Note
          </PrimaryButton>
        </ModalBottomButtonContainer>
      </FluentProvider>
    </FluentModal>
  );
};
